import React from 'react';
import { Trash2, Clock } from 'lucide-react';
import type { ModalProps } from '../../types/components';
import type { HistoryItem } from '../../types/usage';

interface HistoryPanelProps extends ModalProps {
  history: HistoryItem[];
  onSelectHistory: (item: HistoryItem) => void;
  onDeleteHistory?: (id: string) => void;
  onClearHistory?: () => void;
}

export const HistoryPanel: React.FC<HistoryPanelProps> = ({
  isOpen,
  onClose,
  history,
  onSelectHistory,
  onDeleteHistory,
  onClearHistory
}) => {
  if (!isOpen) return null;

  const handleSelectHistory = (item: HistoryItem) => {
    onSelectHistory(item);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-600" />
            <h2 className="text-2xl font-bold">History</h2>
          </div>
          <div className="flex items-center gap-2">
            {onClearHistory && history.length > 0 && (
              <button
                onClick={onClearHistory}
                className="px-3 py-1 text-sm text-red-600 hover:text-red-700 border border-red-300 rounded"
              >
                Clear All
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl"
            >
              ✕
            </button>
          </div>
        </div>
        
        <div className="mb-6 text-center">
          <p className="text-xs text-gray-400">Last update: 8/19/2026</p>
          <p className="text-xs text-gray-400">Now Mobile Friendly</p>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Clock className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No history yet</p>
              <p className="text-sm">Your conversation history will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors group"
                  onClick={() => handleSelectHistory(item)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">
                          {item.timestamp}
                        </span>
                        {item.model && (
                          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                            {item.model}
                          </span>
                        )}
                        <span className={`text-xs px-2 py-1 rounded ${
                          item.rating === 'thumbs_up' 
                            ? 'text-green-600 bg-green-100' 
                            : item.rating === 'thumbs_down'
                            ? 'text-red-600 bg-red-100'
                            : 'text-gray-600 bg-gray-100'
                        }`}>
                          {item.rating === 'thumbs_up' 
                            ? '👍 Positive' 
                            : item.rating === 'thumbs_down'
                            ? '👎 Negative'
                            : 'No Rating'
                          }
                        </span>
                      </div>
                      
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-900 mb-1">Input:</p>
                        <p className="text-sm text-gray-700 line-clamp-2 bg-gray-50 p-2 rounded">
                          {item.input}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium text-gray-900 mb-1">Output:</p>
                        <p className="text-sm text-gray-600 line-clamp-3 bg-blue-50 p-2 rounded">
                          {item.output}
                        </p>
                      </div>
                    </div>
                    
                    {onDeleteHistory && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteHistory(item.id);
                        }}
                        className="ml-4 p-2 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete this item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};